/** @jsx jsx */
import { Fragment, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { useCart, useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { Button, Flex, jsx, Spinner, Text } from 'theme-ui'
import { useAddToCart } from '~/hooks/components/use-add-to-cart'
import { useAria } from '~/hooks/components/use-aria'
import { useCanClub } from '~/hooks/components/use-can-club'
import { useCartFlyout } from '~/hooks/components/use-cart-flyout'
import { useEmptyCart } from '~/hooks/components/use-empty-cart'
import { useGeolocation } from '~/hooks/components/use-geolocation'
import { usePostPromoPrice } from '~/hooks/components/use-post-promo-price'
import { applyPromoCodesFromStorage } from '~/hooks/components/use-promo-codes'
import { METADATA_BULK_LINE_ITEM } from '~/utils/bulk'
import { toUsdCurrency } from '~/utils/formatters'
import { getClubDiscountedPrice } from '~/utils/subscriptions'

const ListingAddToCartButton = ({
  checkPostPromoPriceCodes = false,
  comingSoon = false,
  isBulk,
  label = null,
  preorderDate = null,
  quantity = 1,
  showPrice = false,
  sku,
  soldOut = false,
}) => {
  const { updateAriaLive } = useAria()
  const translate = useTranslate()
  const { clubDiscount } = useCanClub()
  const { addPromoCode, addToCart, cart } = useCart()
  const [buttonIsLoading, setButtonIsLoading] = useState(false)
  const [apiError, setApiError] = useState(null)
  const { openCartFlyout } = useCartFlyout()
  const cartFlyoutTrigger = useRef()

  const { getAddToCartDataBySku } = useAddToCart()
  const { emptyBulkCart, emptyClubCart } = useEmptyCart()

  const {
    parts,
    postPromoPrice,
    postPromoPrice24,
    postPromoPriceCodes,
    price,
  } = getAddToCartDataBySku(sku) || {}

  const [displayPrice, setDisplayPrice] = useState(price)
  const bestPostPromoPrice = usePostPromoPrice({
    order: cart,
    postPromoPrice,
    postPromoPrice24,
    postPromoPriceCodes: checkPostPromoPriceCodes && postPromoPriceCodes,
    price,
  })
  const displayPostPromoPrice =
    bestPostPromoPrice && bestPostPromoPrice < displayPrice
      ? bestPostPromoPrice
      : null

  const { checkGeolocation } = useGeolocation()

  const lineItemMetadata = {}
  if (isBulk) {
    lineItemMetadata[METADATA_BULK_LINE_ITEM] = true
  }

  let displayLabel = translate('product.add_to_cart')
  if (comingSoon) {
    displayLabel = translate('product.coming_soon')
  } else if (soldOut) {
    displayLabel = translate('product.sold_out')
  } else if (label) {
    displayLabel = label
  } else if (preorderDate && moment(preorderDate).isAfter(moment())) {
    displayLabel = translate('product.preorder')
  }

  useEffect(() => {
    if (!postPromoPrice && clubDiscount) {
      setDisplayPrice(getClubDiscountedPrice(clubDiscount, price))
    } else {
      setDisplayPrice(price)
    }
  }, [clubDiscount, postPromoPrice, price])

  const handleSubmit = async (event) => {
    if (event) event.preventDefault()
    if (comingSoon || soldOut) return

    openCartFlyout(cartFlyoutTrigger)

    setButtonIsLoading(true)
    setApiError(null)

    try {
      await emptyBulkCart(isBulk, true)
      await emptyClubCart(true)
      await addToCart({
        sku,
        quantity,
        options: { parts },
        metadata: lineItemMetadata,
      })
    } catch (error) {
      setApiError(translate('error.api.default'))
      console.error(error, {
        source: 'ListingAddToCartButton.handleSubmit.addToCart',
      })
    }

    try {
      applyPromoCodesFromStorage({ addPromoCode })
    } catch (error) {}

    setButtonIsLoading(false)
    updateAriaLive('Product added to cart.')

    try {
      checkGeolocation()
    } catch (error) {}
  }

  if (!sku) {
    return <Fragment />
  }

  return (
    <form onSubmit={handleSubmit}>
      <Button
        disabled={comingSoon || soldOut}
        aria-label={`Add to cart, ${
          displayPostPromoPrice
            ? toUsdCurrency(displayPostPromoPrice)
            : toUsdCurrency(displayPrice)
        }`}
        type="submit"
        variant="primary"
        sx={{
          width: '100%',
          display: 'block',
        }}
        ref={cartFlyoutTrigger}
      >
        {buttonIsLoading && (
          <Spinner
            data-testid="spinner"
            size="24"
            color="inherit"
            mb={-1}
            sx={{
              position: 'relative',
              top: '-2px',
              '@media (prefers-reduced-motion)': {
                circle: {
                  animationIterationCount: 8,
                },
              },
            }}
          />
        )}
        <Flex
          mx={postPromoPrice && [-2, null, -1]}
          sx={{
            justifyContent: postPromoPrice ? 'space-between' : 'center',
          }}
        >
          {!buttonIsLoading && <Text as="span">{displayLabel}</Text>}
          {showPrice && !buttonIsLoading && (
            <Text as="span" ml={1}>
              {displayPostPromoPrice && (
                <Text as="span">
                  <Text
                    as="span"
                    sx={{
                      textDecoration: 'line-through',
                      // @todo Use a container query once supported by theme-ui.
                      '@media only screen and (max-width: 379px)': {
                        display: 'none',
                      },
                    }}
                    aria-label={`Normally ${displayPrice}`}
                  >
                    {toUsdCurrency(displayPrice)}
                  </Text>{' '}
                  {toUsdCurrency(displayPostPromoPrice)}
                </Text>
              )}
              {!displayPostPromoPrice && (
                <Text as="span">
                  <Text as="span" mr={1}>
                    —
                  </Text>
                  {toUsdCurrency(displayPrice)}
                </Text>
              )}
            </Text>
          )}
        </Flex>
      </Button>
      {apiError && (
        <Text as="p" color="error" variant="formError" mt={2}>
          {apiError.toString()}
        </Text>
      )}
    </form>
  )
}

export default ListingAddToCartButton
